/* eslint-disable react/prop-types */
import React, { useState, useRef, useLayoutEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Square, CheckSquare } from 'lucide-react';
import './PqtFormMultiSelect.css';

const MENU_Z = 10120;

function normalizeOptions(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((o) => {
      if (o != null && typeof o === 'object' && 'value' in o) {
        const label = o.label != null ? String(o.label) : String(o.value);
        return { ...o, label };
      }
      return { value: o, label: String(o) };
    })
    .filter((o) => o.value !== undefined && o.value !== null && o.value !== '');
}

/**
 * Multi-select (IDs) with CRC-style menu portaled above PQT modals.
 */
export default function PqtFormMultiSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select',
  disabled = false,
  className = '',
}) {
  const opts = useMemo(() => normalizeOptions(options), [options]);
  const ids = Array.isArray(value) ? value : [];
  const hasId = (id) => ids.some((x) => x === id || String(x) === String(id));
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState({});

  const updatePosition = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const maxH = Math.min(320, Math.max(120, window.innerHeight - r.bottom - 16));
    setMenuStyle({
      position: 'fixed',
      top: Math.round(r.bottom + 4),
      left: Math.round(r.left),
      width: Math.round(r.width),
      maxHeight: maxH,
      zIndex: MENU_Z,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, updatePosition]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (wrapRef.current?.contains(t)) return;
      if (t.closest && t.closest('.pqt-form-multi__menu')) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (id) => {
    const next = hasId(id) ? ids.filter((x) => x !== id && String(x) !== String(id)) : [...ids, id];
    onChange(next);
  };

  const display = useMemo(() => {
    if (!ids.length) return '';
    const labels = ids
      .map((id) => opts.find((o) => o.value === id || String(o.value) === String(id))?.label)
      .filter(Boolean);
    if (!labels.length) return `${ids.length} selected`;
    if (labels.length <= 2) return labels.join(', ');
    return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
  }, [ids, opts]);

  const menu =
    open && !disabled && opts.length > 0 ? (
      <ul className="pqt-form-multi__menu" role="listbox" style={menuStyle}>
        {opts.map((opt) => {
          const checked = hasId(opt.value);
          return (
            <li
              key={String(opt.value)}
              role="option"
              aria-selected={checked}
              className={`pqt-form-multi__option${checked ? ' pqt-form-multi__option--active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                toggle(opt.value);
              }}
            >
              {checked ? (
                <CheckSquare size={18} strokeWidth={2} className="pqt-form-multi__check pqt-form-multi__check--on" aria-hidden />
              ) : (
                <Square size={18} strokeWidth={2} className="pqt-form-multi__check" aria-hidden />
              )}
              <span className="pqt-form-multi__label">{opt.label}</span>
            </li>
          );
        })}
      </ul>
    ) : null;

  return (
    <div className={`pqt-form-multi ${className}`.trim()} ref={wrapRef}>
      <button
        ref={btnRef}
        type="button"
        className={`pqt-form-multi__trigger${open ? ' pqt-form-multi__trigger--open' : ''}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((v) => !v)}
      >
        <span
          className={`pqt-form-multi__value${display === '' ? ' pqt-form-multi__value--placeholder' : ''}`}
        >
          {display || placeholder}
        </span>
        <ChevronDown className="pqt-form-multi__chev" size={18} strokeWidth={2} aria-hidden />
      </button>
      {menu && typeof document !== 'undefined' ? createPortal(menu, document.body) : null}
    </div>
  );
}
